import React, { useCallback, useState } from "react";

import { Card } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

import classNames from "classnames";
import { isBoolean } from "lodash";
import { useDropzone } from "react-dropzone";

import { type Participant, ParticipantTypes, type RssDocument } from "@api/graphql";
import { DrawerUploadIcon, RightArrowIcon } from "@assets/icons";
import type { ParticipantWithSinglePseudonym } from "@components/account_profile/ParticipantDropdown";
import { FileDisplay } from "@components/grouped_resales/FileDisplay";
import { ReporterDisplay } from "@components/grouped_resales/ReporterDisplay";
import { useIsAuthorisedUser } from "@components/permission_control/ProtectedComponent";
import type { CsvSubmissionFormData } from "@pages/dashboard/ResaleCsvSubmission";
import { isCMO } from "@util/index";

import { ROUTES } from "../../../routes";
import { IconButton } from "../../IconButton";
import { IconLink } from "../../IconLink";
import type { FormStepProps } from "../../form/MultiStepForm";

import { DownloadexcelLink } from "./DownloadexcelLink";

import styles from "./UploadCsv.module.scss";

export interface CsvInput {
  file?: File;
  participant?: ParticipantWithSinglePseudonym | null;
}

export interface UploadCsvProps extends FormStepProps<CsvSubmissionFormData, CsvInput> {
  sourceFile: RssDocument | undefined;
  participant: Participant | undefined;
}

export const UploadCsv = ({ onSubmit, sourceFile, participant }: UploadCsvProps) => {
  const [showFileError, setShowFileError] = useState<boolean>();
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(acceptedFiles[0]);
      reader.onabort = () => console.log("file reading was aborted");
      reader.onerror = () => console.log("file reading has failed");
      reader.onload = () => {
        const file = acceptedFiles[0];
        const isAcceptedType =
          file.type === "text/csv" ||
          file.type === "application/vnd.ms-excel" ||
          file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        if (!sourceFile || isAcceptedType) {
          onSubmit({ file: acceptedFiles[0] });
        } else {
          setShowFileError(true);
        }
      };
    },
    [onSubmit, sourceFile],
  );
  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });
  const isCMOParticipant =
    useIsAuthorisedUser({ requiredParticipantTypes: [ParticipantTypes.CMO] }) ||
    isBoolean(participant?.participant_types && isCMO(participant.participant_types));

  return (
    <Container fluid className={classNames(styles["container"])}>
      <Row className="mb-5">
        <Col>
          <h2>Upload file</h2>
        </Col>

        {participant !== undefined ? (
          <>
            <h5 className="mt-1">
              for <b>{participant.full_name}</b>
            </h5>
            <p
              className="b4 mt-1"
              style={{ maxWidth: "30rem", marginLeft: "auto", marginRight: "auto", marginBottom: "-1rem" }}
            >
              Please upload the file exactly as the customer submitted it, prior to any file manipulation or formatting
              that is required.
            </p>
          </>
        ) : (
          <></>
        )}
      </Row>

      {sourceFile ? (
        <Row>
          <Col className={classNames(styles["card-col"])}>
            <Card className={classNames(styles["card-alt"])}>
              <Row>
                <Col>
                  <ReporterDisplay reporter={sourceFile.participant_name} company={sourceFile.participant_company} />
                </Col>
                <Col>
                  <div className="mt-3">
                    <FileDisplay s3_location={sourceFile.s3_location!} date_created={sourceFile.date_modified} />
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <Row>
        <Col className={classNames(styles["card-col"])}>
          <Card {...getRootProps()} className={classNames(styles["card"])}>
            <input {...getInputProps()} />
            <Card.Body>
              <div className="w-100 d-flex justify-content-center pb-4">
                <IconButton className="align-middle" variant="quaternary" src={DrawerUploadIcon} />
              </div>
              <Card.Title>Click here or drag file to upload</Card.Title>
              <Card.Text className={classNames(styles["card-text"])}>Accepted file types: .csv, .xls, .xlsx</Card.Text>
              <Card.Text className={classNames(styles["card-text"])}>Max file size: 10 MB</Card.Text>
              <Card.Text>Max row allowed: 1000</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {sourceFile ? (
        <></>
      ) : (
        <Row>
          <Col className="d-flex justify-content-center gap-4">
            <DownloadexcelLink isCMO={isCMOParticipant} />
            {!isCMOParticipant && (
              <IconLink
                to={ROUTES.HowToUseTemplate.path}
                src={RightArrowIcon}
                text="How to use the template"
                iconPosition="right"
                target="_blank"
              />
            )}
          </Col>
        </Row>
      )}

      {showFileError ? (
        <p className={classNames(styles["error-text"])}>Only .csv, .xls, .xlsx files are allowed</p>
      ) : (
        <></>
      )}
    </Container>
  );
};
